import { datadogRum } from '@datadog/browser-rum'
import jsonMask from 'json-mask'
import { unref } from 'vue'
import { activeUseApiFetchRequests } from '@/composables/useApiFetch'
import { shouldSuppressDatadogApiFetchResource } from '~~/layers/base/utils/shouldSuppressDatadogApiFetchResource'

type DatadogRumCfg = {
    applicationId: string
    clientToken: string
    env: string
    rum: boolean
    err: boolean
    sessionSampleRate: number
    sessionReplaySampleRate: number
}

const parseDatadogCfg = (value: unknown): DatadogRumCfg | null => {
    if (!value || typeof value !== 'object') return null
    const cfg = value as DatadogRumCfg
    if (!cfg.applicationId || cfg.applicationId === 'none' || !cfg.clientToken) return null
    return cfg
}

/** RUM credentials from `runtimeConfig.public.datadog` (`generate:env` → `NUXT_DATADOG_RESOLVED_CONFIG` at build). */
const resolveClientDatadogCfg = (publicCfg: { datadog?: object }): DatadogRumCfg | null =>
    parseDatadogCfg(publicCfg.datadog)

export default defineNuxtPlugin((nuxtApp) => {
    const runtimeConfig = useRuntimeConfig() as {
        public?: {
            datadog: object
            appVersion: string
            buildHash: string
            /** RUM `service` — `datadogService` from nuxt.config (web / Capacitor env keys). */
            datadogService?: string
        }
    }
    const pinia = usePinia()
    const cfg = resolveClientDatadogCfg(runtimeConfig.public ?? {})

    if (!cfg) {
        return
    }

    datadogRum.init({
        applicationId: cfg.applicationId,
        clientToken: cfg.clientToken,
        service: runtimeConfig.public?.datadogService ?? 'designer',
        site: 'datadoghq.com',
        env: cfg.env,
        version: `${runtimeConfig.public?.appVersion}-${runtimeConfig.public?.buildHash}`,

        sessionSampleRate: cfg.rum || cfg.err ? cfg.sessionSampleRate || 100 : 0,
        sessionReplaySampleRate: cfg.rum ? cfg.sessionReplaySampleRate || 100 : 0,
        trackSessionAcrossSubdomains: true,
        trackUserInteractions: cfg.rum ? true : false,
        trackResources: cfg.err ? true : false,
        trackLongTasks: cfg.err ? true : false,
        trackingConsent: cfg.rum ? 'granted' : 'not-granted',
        trackViewsManually: cfg.rum ? false : true,
        defaultPrivacyLevel: 'allow',

        beforeSend: (event) => {
            // Ignore internal Datadog RUM/logs API requests
            if (
                event.type === 'resource' &&
                event.resource.type === 'fetch' &&
                event.resource.url.includes('datadoghq')
            ) {
                return false // skip sending this event
            }
            // Suppress intermediate retry failures from useApiFetch
            // DataDog RUM automatically tracks resource errors (failed HTTP requests) when trackResources: true
            // When $fetch retries, each failed attempt is tracked as a resource error
            // We suppress intermediate retry failures so only the final error (after all retries) is reported
            // Reference: https://docs.datadoghq.com/real_user_monitoring/browser/modifying_data_and_context/?tab=npm#before-send
            if (
                event.type === 'resource' &&
                event.resource.type === 'fetch' &&
                import.meta.client &&
                shouldSuppressDatadogApiFetchResource(
                    event.resource.url,
                    event.resource.statusCode,
                    activeUseApiFetchRequests
                )
            ) {
                // Intermediate retry failure — skip. After useApiFetch removes the URL from
                // activeUseApiFetchRequests, the final resource event is allowed through.
                return false
            } else if (event.type === 'error' || event.type === 'action') {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const allStores = (pinia as any)._s as Map<string, any>
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                let storesWithState: Record<string, any> = {}

                // excludedStores is used to keep the Datadog context light
                const excludedStores = ['errorRecovery', 'productPreview']

                for (const [key, value] of allStores.entries()) {
                    if (excludedStores.includes(key)) {
                        continue
                    }
                    if (value?.$state['ddErrorContextMask']) {
                        // apply the mask against the store states
                        const fieldsMask = value?.$state['ddErrorContextMask']
                        const limitedState = jsonMask(unref(value.$state), fieldsMask)

                        storesWithState = {
                            [key]: limitedState,
                            ...storesWithState,
                        }
                    } else {
                        storesWithState = {
                            [key]: { ...unref(value.$state) },
                            ...storesWithState,
                        }
                    }
                }

                event.context = {
                    ...event.context,
                    pinia_stores: { ...storesWithState },
                }
            }
            return true
        },
    })

    if (cfg.rum) {
        datadogRum.startSessionReplayRecording()
    }

    nuxtApp.provide('datadog', datadogRum)
})
